import {
  Contract,
  Document,
  Enterprise,
  Insured,
  Population,
  Term,
} from '../models/models';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CapacitorDataStorageSqlite } from 'capacitor-data-storage-sqlite';
import { ApiService } from './api.service';
import { EnterpriseService } from './enterprise.service';
import { PopulationService } from './population.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { DocumentService } from './document.service';

export interface DataInterface {
  enterprises: Enterprise[];
  populations: Population[];
  documents: Document[];
}

export const DATA_CLASSES = {
  enterprises: (e) => e.map((s) => new Enterprise(s)),
  documents: (d) => d.map((s) => new Document(s)),
};

@Injectable({
  providedIn: 'root',
})
export class DataService {
  store: any;
  platform: string;
  state: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  enterprises = new BehaviorSubject<Enterprise[]>(null);
  populations = new BehaviorSubject<Population[]>(null);
  contracts = new BehaviorSubject<any[]>(null);
  selectedEnterprise = new BehaviorSubject<Enterprise>(null);
  /** @deprecated we use the documentService instead */
  documents = new BehaviorSubject<Document[]>(null);

  constructor(
    private apiService: ApiService,
    private httpClient: HttpClient,
    private populationService: PopulationService,
    private enterpriseService: EnterpriseService,
    private documentService: DocumentService
  ) {}

  loadData(enterpriseId: string): Promise<any> {
    return new Promise<any>(async (resolve) => {
      // Load from API
      const promises = [
        this.enterpriseService.getEnterprises(),
        enterpriseId
          ? this.populationService.getPopulations(enterpriseId)
          : null,
        enterpriseId
          ? this.documentService.getDocuments(
              'enterprise',
              enterpriseId,
              null,
              'createdAt',
              'desc'
            )
          : null,
        enterpriseId ? this.populationService.getAllMembers() : null,
        enterpriseId ? this.enterpriseService.getContracts(enterpriseId) : null,
      ].filter((p) => p !== null);

      const [enterprises, populations, documents, insureds, contracts] =
        await Promise.all(promises);
      // TODO : Use subject behavior instead of setItem
      // await this.setItem('enterprises', enterprises);
      // await this.setItem('populations', populations || []);
      // await this.setItem('documents', documents || []);
      // await this.setItem('contracts', contracts || []);

      this.enterprises.next((enterprises as Enterprise[]) || null);
      this.documents.next((documents as Document[]) || null);
      this.contracts.next((contracts as Contract[]) || null);
      this.populations.next((populations as Population[]) || null);

      // Set selected enterprise
      this.selectedEnterprise.next(
        enterprises.find((e) => e.id === enterpriseId) || null
      );

      this.populationService.insureds.next((insureds as Insured[]) || null);
      //this.setItem('documentCategories', documentCategories);

      this.state.next('loaded');
      resolve({ state: 'loaded' });
    });
  }

  async init(): Promise<void> {
    this.platform = Capacitor.getPlatform();
    this.store = CapacitorDataStorageSqlite;
    await this.openStore();
    console.log('Init storage', this.platform);
  }

  reset() {
    this.state.next(null);
    this.selectedEnterprise.next(null);
    this.enterprises.next(null);
    this.populations.next(null);
    this.contracts.next(null);
  }

  async openStore(
    _dbName?: string,
    _table?: string,
    _encrypted?: boolean,
    _mode?: string
  ) {
    if (this.store != null) {
      const database: string = _dbName ? _dbName : 'storage';
      const table: string = _table ? _table : 'storage_table';
      const encrypted: boolean = _encrypted ? _encrypted : false;
      const mode: string = _mode ? _mode : 'no-encryption';
      try {
        await this.store.openStore({ database, table, encrypted, mode });
      } catch (err: any) {
        console.log(err?.error);
      }
    } else {
      console.log('Store not opened !');
    }
  }

  async setItem(key: string, value: any, store = false) {
    value = JSON.stringify(value);
    if (store) {
      await this.store.set({ key, value });
    }
  }

  async getItem<T>(key: string): Promise<T> {
    let obj = await this.store.get({ key });
    return obj?.value !== '' ? JSON.parse(obj.value) : null;
  }

  async removeItem(key: string) {
    await this.store.remove({ key });
  }

  async clear() {
    await this.store.clear();
  }

  async setMembers(members: Insured[]) {
    this.populationService.insureds.next(members);
  }

  getTerms(type: 'usage' | 'privacy'): Observable<Term[]> {
    return this.apiService
      .get<Term[]>('terms/' + type, false)
      .pipe(map((terms) => terms.map((t) => new Term(t))));
  }
}
